import classNames from 'classnames';

import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './TitleIcon.module.scss';

/**
 * The colour family of the disc. The first six match MetricCard's tones (a tone names a
 * colour, not a verdict); the `priority` ones are the operational task severities.
 */
export type TitleIconTone =
    | 'success'
    | 'info'
    | 'neutral'
    | 'caution'
    | 'warning'
    | 'danger'
    | 'priorityCritical'
    | 'priorityHigh'
    | 'priorityWarning'
    | 'priorityLow';

export interface TitleIconProps {
    icon: IconComponent;
    tone?: TitleIconTone;
    /** The glyph's size in pixels inside the 40px disc. */
    iconSize?: number;
    /** Applied to the `<svg>`, for an icon set that needs a per-path override. */
    iconClassName?: string;
    /** Spacing where the disc sits. */
    className?: string;
}

/**
 * An icon on a round tinted background: a card header's `icon` slot, a metric's icon, a task's
 * priority. The tint and the foreground come from the theme tokens for each tone, so the disc
 * follows the theme without a runtime colour mix.
 */
export default function TitleIcon(props: TitleIconProps) {
    const { icon, tone = 'success', iconSize = 24, iconClassName, className } = props;

    return (
        <div className={classNames(styles.titleIcon, className)} data-tone={tone}>
            <SvgIcon
                icon={icon}
                size="custom"
                customSize={iconSize}
                className={classNames(styles.icon, iconClassName)}
                // Decorative: the title, label or value beside the disc says what it is.
                svgProps={{ 'aria-hidden': true }}
            />
        </div>
    );
}
